import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AdminInfraService,
  CreateInfraPayload,
  ModelRequirements,
  VastTemplate,
  VllmConfigSuggestion,
} from "../../services/AdminInfraService";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Tooltip } from "../ui/tooltip";
import { InfoIcon, SearchIcon, RefreshCwIcon } from "lucide-react";
import AiStars from "../../assets/svg/ai-creation.svg?react";
import {
  TemplateKwargsRow,
  templateKwargsObjectFromRows,
} from "./vllmTemplateKwargs";

const GPU_PROFILES = [
  { id: "B200_SXM", label: "B200 SXM (192GB HBM3e)" },
  { id: "H100_SXM", label: "H100 SXM (80GB HBM3)" },
  { id: "H200_SXM", label: "H200 SXM (141GB HBM3e)" },
  { id: "L40S", label: "L40S (48GB GDDR6)" },
];

const GPU_COUNTS = [1, 2, 4, 8];

interface ExtraArgRow {
  key: string;
  value: string;
}

interface CreateInstanceWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiKey: ApiKeyOrGetter;
  onCreated: () => void;
}

/** HF repo id is `namespace/model-name`; use everything after the first `/` for display/base. */
function deriveNameFromHfModelId(raw: string): string | null {
  const t = raw.trim();
  const i = t.indexOf("/");
  if (i <= 0 || i >= t.length - 1) return null;
  const ns = t.slice(0, i).trim();
  const rest = t.slice(i + 1).trim();
  if (!ns || !rest) return null;
  return rest;
}

const CreateInstanceWizard: React.FC<CreateInstanceWizardProps> = ({
  open,
  onOpenChange,
  apiKey,
  onCreated,
}) => {
  const [step, setStep] = useState(0);
  const [provider, setProvider] = useState("vast");
  const [gpuProfile, setGpuProfile] = useState("H100_SXM");
  const [gpuCount, setGpuCount] = useState(1);
  const [hfModelId, setHfModelId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [baseModel, setBaseModel] = useState("");
  const identityAutoSuffixRef = useRef<string | null>(null);
  const [templateHashId, setTemplateHashId] = useState("");
  const [maxModelLen, setMaxModelLen] = useState("16384");
  const [maxNumSeqs, setMaxNumSeqs] = useState("64");
  const [maxNumBatchedTokens, setMaxNumBatchedTokens] = useState("16384");
  const [kvCacheDtype, setKvCacheDtype] = useState("fp8");
  const [loadFormat, setLoadFormat] = useState("auto");
  const [tokenizerMode, setTokenizerMode] = useState("auto");
  const [configFormat, setConfigFormat] = useState("auto");
  const [tensorParallelSize, setTensorParallelSize] = useState("");
  const [gdnPrefillBackend, setGdnPrefillBackend] = useState("auto");
  const [trustRemoteCode, setTrustRemoteCode] = useState(true);
  const [enableLora, setEnableLora] = useState(true);
  const [enableAutoToolChoice, setEnableAutoToolChoice] = useState(true);
  const [toolCallParser, setToolCallParser] = useState("llama3_json");
  const [specMethod, setSpecMethod] = useState("none");
  const [specNumTokens, setSpecNumTokens] = useState("2");
  const [specDraftModel, setSpecDraftModel] = useState("");
  const [specDraftTp, setSpecDraftTp] = useState("");
  const [downloadDir, setDownloadDir] = useState("");
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [chatTemplateKwargsRows, setChatTemplateKwargsRows] = useState<
    TemplateKwargsRow[]
  >([]);
  const [extraArgs, setExtraArgs] = useState<ExtraArgRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState<VastTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTemplatesLoading(true);
    AdminInfraService.getTemplates(null, apiKey).subscribe(
      (data: VastTemplate[]) => {
        setTemplates(data);
        setTemplatesLoading(false);
      },
      () => {
        toast.error("Failed to load Vast templates");
        setTemplatesLoading(false);
      }
    );
  }, [open, apiKey]);

  const [reqsQuery, setReqsQuery] = useState("");
  const [reqs, setReqs] = useState<ModelRequirements | null>(null);
  const [reqsLoading, setReqsLoading] = useState(false);
  const [reqsError, setReqsError] = useState("");

  const lookupRequirements = () => {
    const q = reqsQuery.trim();
    if (!q) return;
    setReqsLoading(true);
    setReqsError("");
    setReqs(null);
    AdminInfraService.getModelRequirements(q, null, apiKey).subscribe(
      (data: ModelRequirements) => { setReqs(data); setReqsLoading(false); },
      (err: any) => {
        setReqsError(err?.response?.message || err?.message || "Model not found");
        setReqsLoading(false);
      }
    );
  };

  const [aiConfigLoading, setAiConfigLoading] = useState(false);
  const [optimizeModalOpen, setOptimizeModalOpen] = useState(false);
  const [optimizeFor, setOptimizeFor] = useState<"throughput" | "latency">(
    "throughput"
  );
  const [estimatedInputTokens, setEstimatedInputTokens] = useState("2000");

  const latencyExtraFlags: ExtraArgRow[] = [
    { key: "--enable-prefix-caching", value: "" },
    { key: "--enable-chunked-prefill", value: "" },
  ];

  const fillWithAi = (mode: "throughput" | "latency") => {
    if (!hfModelId.trim()) {
      toast.error("Enter an HF model id first (Step 3 top field)");
      return;
    }
    setOptimizeModalOpen(false);
    setAiConfigLoading(true);
    const inputTokens = parseInt(estimatedInputTokens) || 2000;
    AdminInfraService.suggestVllmConfig(hfModelId.trim(), mode, inputTokens, null, apiKey).subscribe(
      (data: VllmConfigSuggestion) => {
        if (data.max_model_len) setMaxModelLen(String(data.max_model_len));
        if (data.max_num_seqs) setMaxNumSeqs(String(data.max_num_seqs));
        if (data.max_num_batched_tokens) setMaxNumBatchedTokens(String(data.max_num_batched_tokens));
        if (data.kv_cache_dtype) setKvCacheDtype(data.kv_cache_dtype);
        if (data.load_format) setLoadFormat(data.load_format);
        if (data.tokenizer_mode) setTokenizerMode(data.tokenizer_mode);
        if (data.config_format) setConfigFormat(data.config_format);
        if (data.gdn_prefill_backend) setGdnPrefillBackend(data.gdn_prefill_backend);
        if (data.enable_auto_tool_choice !== undefined) setEnableAutoToolChoice(data.enable_auto_tool_choice);
        if (data.tool_call_parser) setToolCallParser(data.tool_call_parser);

        if (mode === "latency") {
          setExtraArgs((prev) => {
            const existing = new Set(prev.map((r) => r.key));
            const toAdd = latencyExtraFlags.filter((f) => !existing.has(f.key));
            return [...prev, ...toAdd];
          });
        } else {
          setExtraArgs((prev) =>
            prev.filter(
              (r) => !latencyExtraFlags.some((f) => f.key === r.key)
            )
          );
        }

        setAiConfigLoading(false);
        toast.success(
          mode === "latency"
            ? "Config optimized for latency (low TTFT)"
            : "Config optimized for throughput"
        );
      },
      (err: any) => {
        setAiConfigLoading(false);
        toast.error(err?.response?.message || "AI config suggestion failed");
      }
    );
  };

  const resetForm = () => {
    setStep(0);
    setProvider("vast");
    setGpuProfile("H100_SXM");
    setGpuCount(1);
    setHfModelId("");
    setDisplayName("");
    setBaseModel("");
    identityAutoSuffixRef.current = null;
    setTemplateHashId("");
    setMaxModelLen("16384");
    setMaxNumSeqs("64");
    setMaxNumBatchedTokens("16384");
    setKvCacheDtype("fp8");
    setLoadFormat("auto");
    setTokenizerMode("auto");
    setConfigFormat("auto");
    setTensorParallelSize("");
    setGdnPrefillBackend("auto");
    setTrustRemoteCode(true);
    setEnableLora(true);
    setEnableAutoToolChoice(true);
    setToolCallParser("llama3_json");
    setSpecMethod("none");
    setSpecNumTokens("2");
    setSpecDraftModel("");
    setSpecDraftTp("");
    setDownloadDir("");
    setHost("");
    setPort("");
    setChatTemplateKwargsRows([]);
    setExtraArgs([]);
  };

  const vllmConfig = useMemo(() => {
    const c: Record<string, unknown> = {};
    if (maxModelLen) c.max_model_len = Number(maxModelLen);
    if (maxNumSeqs) c.max_num_seqs = Number(maxNumSeqs);
    if (maxNumBatchedTokens)
      c.max_num_batched_tokens = Number(maxNumBatchedTokens);
    if (kvCacheDtype && kvCacheDtype !== "auto") c.kv_cache_dtype = kvCacheDtype;
    if (loadFormat && loadFormat !== "auto") c.load_format = loadFormat;
    if (tokenizerMode && tokenizerMode !== "auto")
      c.tokenizer_mode = tokenizerMode;
    if (configFormat && configFormat !== "auto") c.config_format = configFormat;
    if (gdnPrefillBackend && gdnPrefillBackend !== "auto") c.gdn_prefill_backend = gdnPrefillBackend;
    if (tensorParallelSize) c.tensor_parallel_size = Number(tensorParallelSize);
    c.trust_remote_code = trustRemoteCode;
    c.enable_lora = enableLora;
    c.enable_auto_tool_choice = enableAutoToolChoice;
    if (toolCallParser) c.tool_call_parser = toolCallParser;
    if (downloadDir) c.download_dir = downloadDir;
    if (host) c.host = host;
    if (port) c.port = Number(port);
    if (specMethod && specMethod !== "none") {
      const spec: Record<string, unknown> = {
        method: specMethod,
        num_speculative_tokens: Number(specNumTokens) || 2,
      };
      if (["draft_model", "eagle", "eagle3", "mlp"].includes(specMethod) && specDraftModel.trim()) {
        spec.model = specDraftModel.trim();
      }
      if (["eagle", "eagle3"].includes(specMethod) && specDraftTp) {
        spec.draft_tensor_parallel_size = Number(specDraftTp);
      }
      c.speculative_config = spec;
    }
    const addl: Record<string, string> = {};
    for (const row of extraArgs) {
      if (row.key.trim()) addl[row.key.trim()] = row.value;
    }
    if (Object.keys(addl).length) c.additional_args = addl;
    const kwargs = templateKwargsObjectFromRows(chatTemplateKwargsRows);
    if (kwargs) c.default_chat_template_kwargs = kwargs;
    return c;
  }, [
    maxModelLen,
    maxNumSeqs,
    maxNumBatchedTokens,
    kvCacheDtype,
    loadFormat,
    tokenizerMode,
    configFormat,
    gdnPrefillBackend,
    tensorParallelSize,
    trustRemoteCode,
    enableLora,
    enableAutoToolChoice,
    toolCallParser,
    specMethod,
    specNumTokens,
    specDraftModel,
    specDraftTp,
    downloadDir,
    host,
    port,
    chatTemplateKwargsRows,
    extraArgs,
  ]);

  const handleClose = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const submit = () => {
    const payload: CreateInfraPayload = {
      provider,
      gpu_profile: gpuProfile,
      gpu_count: gpuCount,
      hf_model_id: hfModelId.trim(),
      vllm_config: vllmConfig,
      max_adapter_count: 8,
      disk_gb: 80,
    };
    if (baseModel.trim()) payload.base_model = baseModel.trim();
    if (displayName.trim()) payload.display_name = displayName.trim();
    if (templateHashId.trim()) payload.template_hash_id = templateHashId.trim();

    setSubmitting(true);
    AdminInfraService.createInstance(payload, null, apiKey).subscribe(
      () => {
        setSubmitting(false);
        toast.success("Instance provisioning started");
        resetForm();
        onOpenChange(false);
        onCreated();
      },
      (err: any) => {
        setSubmitting(false);
        console.error(err);
        const msg =
          typeof err === "string"
            ? err
            : err?.response?.message || err?.message || "Unknown error";
        toast.error(`Failed to create instance: ${msg}`);
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New inference instance</DialogTitle>
        </DialogHeader>

        {step === 0 && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Step 1 — Provider</p>
            <div>
              <Label>Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vast">Vast</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Step 2 — GPU</p>
            <div>
              <Label className="text-xs text-muted-foreground">Check requirements</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={reqsQuery}
                  onChange={(e) => setReqsQuery(e.target.value)}
                  placeholder="org/model-name"
                  onKeyDown={(e) => e.key === "Enter" && lookupRequirements()}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  disabled={reqsLoading || !reqsQuery.trim()}
                  onClick={lookupRequirements}
                >
                  <SearchIcon className="w-4 h-4 mr-1" />
                  {reqsLoading ? "..." : "Lookup"}
                </Button>
              </div>
              {reqsError && (
                <p className="text-xs text-destructive mt-1">{reqsError}</p>
              )}
              {reqs && (
                <div className="mt-3 rounded-md border border-border bg-muted/50 p-3 text-xs space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Parameters</span>
                    <span className="font-gc-med">{reqs.parameters_human} ({reqs.dtype})</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Weight size</span>
                    <span className="font-gc-med">{reqs.weight_size_gb} GB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Est. VRAM (BF16)</span>
                    <span className="font-gc-med">{reqs.estimated_vram_bf16_gb} GB</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Est. VRAM (FP8 KV)</span>
                    <span className="font-gc-med">{reqs.estimated_vram_fp8_gb} GB</span>
                  </div>
                  {reqs.recommendations.length > 0 && (
                    <>
                      <div className="border-t border-border pt-2 mt-2">
                        <span className="text-muted-foreground">Recommended configs:</span>
                      </div>
                      {reqs.recommendations.map((r, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`w-full flex justify-between items-center rounded px-2 py-1.5 transition-colors text-left ${
                            r.available
                              ? "hover:bg-muted"
                              : "opacity-50 cursor-not-allowed"
                          }`}
                          disabled={!r.available}
                          onClick={() => {
                            setGpuProfile(r.gpu_profile);
                            setGpuCount(r.gpu_count);
                          }}
                        >
                          <span className="flex items-center gap-1.5">
                            <span
                              className={`inline-block w-2 h-2 rounded-full ${
                                r.available ? "bg-green-500" : "bg-red-400"
                              }`}
                            />
                            {r.gpu_count}x {GPU_PROFILES.find((g) => g.id === r.gpu_profile)?.label || r.gpu_profile}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {r.total_vram_gb} GB
                            {r.min_dph != null && ` · $${r.min_dph.toFixed(2)}/hr`}
                          </span>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            <div>
              <Label>GPU type</Label>
              <Select value={gpuProfile} onValueChange={setGpuProfile}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GPU_PROFILES.map((g) => {
                    const info = reqs?.gpu_availability?.[g.id]?.[gpuCount];
                    const hasOffers = info === undefined || info.available;
                    return (
                      <SelectItem key={g.id} value={g.id}>
                        <span className="flex items-center gap-1.5">
                          {reqs?.gpu_availability && (
                            <span
                              className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                                hasOffers ? "bg-green-500" : "bg-red-400"
                              }`}
                            />
                          )}
                          {g.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>GPU count</Label>
              <Select
                value={String(gpuCount)}
                onValueChange={(v) => setGpuCount(Number(v))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GPU_COUNTS.map((n) => {
                    const info = reqs?.gpu_availability?.[gpuProfile]?.[n];
                    const hasOffers = info === undefined || info.available;
                    return (
                      <SelectItem key={n} value={String(n)}>
                        <span className="flex items-center gap-1.5">
                          {reqs?.gpu_availability && (
                            <span
                              className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                                hasOffers ? "bg-green-500" : "bg-red-400"
                              }`}
                            />
                          )}
                          {n}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 py-2 px-1 max-h-[55vh] overflow-y-auto">
            <p className="text-sm text-muted-foreground">Step 3 — Model &amp; vLLM</p>

            {/* Model Identity */}
            <div>
              <div className="flex items-center gap-1.5">
                <Label>HF model id</Label>
                <Tooltip content="Hugging Face model repo (e.g. meta-llama/Llama-3.1-8B-Instruct). This is passed to vLLM as the --model argument.">
                  <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>
              <Input
                className="mt-1"
                value={hfModelId}
                onChange={(e) => {
                  const v = e.target.value;
                  setHfModelId(v);
                  const suffix = deriveNameFromHfModelId(v);
                  if (!suffix) return;
                  const prev = identityAutoSuffixRef.current;
                  const d = displayName.trim();
                  const b = baseModel.trim();
                  const bothEmpty = !d && !b;
                  const bothStillAuto =
                    prev !== null && d === prev && b === prev;
                  if (bothEmpty || bothStillAuto) {
                    setDisplayName(suffix);
                    setBaseModel(suffix);
                    identityAutoSuffixRef.current = suffix;
                  }
                }}
                placeholder="org/model-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">Display Name</Label>
                  <Tooltip content="The name shown in Maitai model dropdowns, routing rules, and API calls. If blank, defaults to Base Model.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Input
                  className="mt-1"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={baseModel || hfModelId.split("/").pop() || "My-Model"}
                />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">Base Model</Label>
                  <Tooltip content="Used to match LoRA adapters to instances. If blank, derived from the last segment of the HF model id.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Input
                  className="mt-1"
                  value={baseModel}
                  onChange={(e) => setBaseModel(e.target.value)}
                  placeholder={hfModelId.split("/").pop() || "Model-Name"}
                />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">Vast template</Label>
                <Tooltip content="Private Vast template with the on-start script. Leave blank to auto-create a standard vLLM + LoRA template.">
                  <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>
              {templatesLoading ? (
                <p className="text-xs text-muted-foreground mt-1">Loading templates...</p>
              ) : templates.length > 0 ? (
                <Select value={templateHashId} onValueChange={setTemplateHashId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Auto (standard vLLM + LoRA)" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.hash_id} value={t.hash_id}>
                        {t.name || t.hash_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  No templates found — a standard template will be created automatically
                </p>
              )}
            </div>

            {/* Divider + vLLM Config section */}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex-1 border-t border-border" />
              <span className="text-xs text-muted-foreground font-gc-med">vLLM Configuration</span>
              <Tooltip content="Auto-fill vLLM config using AI based on the HF model id">
                <button
                  type="button"
                  className="p-1.5 rounded-md text-maitai-green hover:bg-maitai-green/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  disabled={aiConfigLoading || !hfModelId.trim()}
                  onClick={() => setOptimizeModalOpen(true)}
                >
                  {aiConfigLoading ? (
                    <RefreshCwIcon className="w-4 h-4 animate-spin" />
                  ) : (
                    <AiStars className="w-4 h-4" />
                  )}
                </button>
              </Tooltip>
              <Dialog
                open={optimizeModalOpen}
                onOpenChange={setOptimizeModalOpen}
              >
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>Optimize for</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="optimize"
                        className="mt-1"
                        checked={optimizeFor === "throughput"}
                        onChange={() => setOptimizeFor("throughput")}
                      />
                      <div>
                        <p className="font-gc-med text-sm">Throughput</p>
                        <p className="text-xs text-muted-foreground">
                          Maximize requests/sec. Higher concurrency and larger
                          batches.
                        </p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="optimize"
                        className="mt-1"
                        checked={optimizeFor === "latency"}
                        onChange={() => setOptimizeFor("latency")}
                      />
                      <div>
                        <p className="font-gc-med text-sm">Latency</p>
                        <p className="text-xs text-muted-foreground">
                          Lowest TTFT. Enables prefix caching, chunked prefill,
                          and multi-step scheduling.
                        </p>
                      </div>
                    </label>
                  </div>
                  <div className="pt-1">
                    <Label className="text-xs text-muted-foreground">Estimated avg input size (tokens)</Label>
                    <Input
                      className="mt-1"
                      type="number"
                      value={estimatedInputTokens}
                      onChange={(e) => setEstimatedInputTokens(e.target.value)}
                      placeholder="2000"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Helps size context window and batching. ~500 for short API calls, ~2K for chat, ~8K+ for RAG/docs.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="ghost"
                      onClick={() => setOptimizeModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button onClick={() => fillWithAi(optimizeFor)}>
                      <AiStars className="w-4 h-4 mr-1.5" />
                      Auto-fill
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <div className="flex-1 border-t border-border" />
            </div>

            {aiConfigLoading ? (
              <div className="space-y-3 animate-pulse">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-9 bg-muted rounded" />
                ))}
              </div>
            ) : (
              <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">max-model-len</Label>
                  <Tooltip content="Maximum context length the model will accept. Lower values free GPU memory for more KV cache slots, improving concurrency. vLLM default: model's full context (often 128K).">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Input
                  className="mt-1"
                  value={maxModelLen}
                  onChange={(e) => setMaxModelLen(e.target.value)}
                  placeholder="16384"
                />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">max-num-seqs</Label>
                  <Tooltip content="Max concurrent sequences in a batch. Lower values reduce decode-step latency and improve TTFT at the cost of throughput. vLLM default: 256.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Input
                  className="mt-1"
                  value={maxNumSeqs}
                  onChange={(e) => setMaxNumSeqs(e.target.value)}
                  placeholder="64"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">max-num-batched-tokens</Label>
                <Tooltip content="Max tokens the scheduler can prefill in one step. Higher values let larger prompts finish in fewer steps, improving TTFT. Should be >= max-model-len.">
                  <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>
              <Input
                className="mt-1"
                value={maxNumBatchedTokens}
                onChange={(e) => setMaxNumBatchedTokens(e.target.value)}
                placeholder="16384"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">kv-cache-dtype</Label>
                  <Tooltip content="Data type for the KV cache. fp8 halves memory usage on H100/H200, doubling effective cache capacity with minimal quality loss.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Select value={kvCacheDtype} onValueChange={setKvCacheDtype}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">auto</SelectItem>
                    <SelectItem value="fp8">fp8</SelectItem>
                    <SelectItem value="fp8_e5m2">fp8_e5m2</SelectItem>
                    <SelectItem value="fp8_e4m3">fp8_e4m3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">load-format</Label>
                  <Tooltip content="Format used to load model weights. 'auto' detects from the checkpoint. Use 'safetensors' for HF models, 'mistral' for Mistral checkpoints.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Select value={loadFormat} onValueChange={setLoadFormat}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">auto</SelectItem>
                    <SelectItem value="safetensors">safetensors</SelectItem>
                    <SelectItem value="pt">pt</SelectItem>
                    <SelectItem value="mistral">mistral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">tokenizer-mode</Label>
                  <Tooltip content="Tokenizer backend. 'auto' uses the fast Rust tokenizer when available. Use 'mistral' for Mistral Tekken tokenizer, 'slow' to force the Python tokenizer.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Select value={tokenizerMode} onValueChange={setTokenizerMode}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">auto</SelectItem>
                    <SelectItem value="slow">slow</SelectItem>
                    <SelectItem value="mistral">mistral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">config-format</Label>
                  <Tooltip content="Model config format. 'auto' detects from the checkpoint. Use 'hf' for Hugging Face models, 'mistral' for Mistral native format.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Select value={configFormat} onValueChange={setConfigFormat}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">auto</SelectItem>
                    <SelectItem value="hf">hf</SelectItem>
                    <SelectItem value="mistral">mistral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">gdn-prefill-backend</Label>
                <Tooltip content="Attention kernel for Qwen3.5 GDN (Gated Delta Networks) models. 'triton' is reliable with no JIT overhead. 'flashinfer' is faster but requires ~3GB temp disk for first-run compilation. Use 'auto' for non-GDN models.">
                  <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>
              <Select value={gdnPrefillBackend} onValueChange={setGdnPrefillBackend}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">auto</SelectItem>
                  <SelectItem value="triton">triton</SelectItem>
                  <SelectItem value="flashinfer">flashinfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">tensor-parallel-size</Label>
                <Tooltip content="Number of GPUs to shard the model across. Defaults to the GPU count selected in step 2. Higher TP reduces per-GPU memory but adds inter-GPU communication overhead.">
                  <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>
              <Input
                className="mt-1"
                value={tensorParallelSize}
                onChange={(e) => setTensorParallelSize(e.target.value)}
                placeholder={String(gpuCount)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="trc"
                checked={trustRemoteCode}
                onCheckedChange={(v) => setTrustRemoteCode(v === true)}
              />
              <Label htmlFor="trc" className="text-sm font-normal cursor-pointer">
                trust-remote-code
              </Label>
              <Tooltip content="Allow executing custom model code from the HF repo (tokenizers, architectures). Required for most newer models (Qwen, Phi, etc.).">
                <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
              </Tooltip>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="elora"
                checked={enableLora}
                onCheckedChange={(v) => setEnableLora(v === true)}
              />
              <Label htmlFor="elora" className="text-sm font-normal cursor-pointer">
                enable-lora
              </Label>
              <Tooltip content="Enable LoRA adapter support. Required for loading fine-tuned adapters onto the base model at runtime.">
                <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
              </Tooltip>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="eatc"
                checked={enableAutoToolChoice}
                onCheckedChange={(v) => setEnableAutoToolChoice(v === true)}
              />
              <Label htmlFor="eatc" className="text-sm font-normal cursor-pointer">
                enable-auto-tool-choice
              </Label>
              <Tooltip content="Let vLLM automatically detect and parse tool/function calls from the model output. Required for tool-use workflows.">
                <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
              </Tooltip>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">tool-call-parser</Label>
                <Tooltip content="Parser that extracts tool calls from model output. Must match the model's tool-call format (e.g. llama3_json for Llama 3, hermes for Hermes, mistral for Mistral).">
                  <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>
              <Select value={toolCallParser} onValueChange={setToolCallParser}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="llama3_json">llama3_json</SelectItem>
                  <SelectItem value="hermes">hermes</SelectItem>
                  <SelectItem value="mistral">mistral</SelectItem>
                  <SelectItem value="pythonic">pythonic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">Default chat template kwargs</Label>
                  <Tooltip content="Builds a JSON object for vLLM --default-chat-template-kwargs (passed to the tokenizer chat template, e.g. enable_thinking). Values are parsed as JSON when valid—use false, true, numbers—or left as plain strings. Leave empty to omit the flag.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setChatTemplateKwargsRows((prev) => [
                      ...prev,
                      { key: "", value: "" },
                    ])
                  }
                >
                  Add pair
                </Button>
              </div>
              <div className="space-y-2 mt-2">
                {chatTemplateKwargsRows.map((row, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <Input
                      className="flex-1 font-mono text-xs"
                      placeholder="Key (e.g. enable_thinking)"
                      value={row.key}
                      onChange={(e) => {
                        const next = [...chatTemplateKwargsRows];
                        next[idx] = { ...next[idx], key: e.target.value };
                        setChatTemplateKwargsRows(next);
                      }}
                    />
                    <Input
                      className="flex-1 font-mono text-xs"
                      placeholder='Value (e.g. false or "text")'
                      value={row.value}
                      onChange={(e) => {
                        const next = [...chatTemplateKwargsRows];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setChatTemplateKwargsRows(next);
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="shrink-0 text-destructive"
                      onClick={() =>
                        setChatTemplateKwargsRows((prev) =>
                          prev.filter((_, i) => i !== idx)
                        )
                      }
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">download-dir</Label>
                <Tooltip content="Directory where model weights are downloaded on the instance. Must be on a writable volume. Vast instances typically use /workspace.">
                  <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>
              <Input
                className="mt-1"
                value={downloadDir}
                onChange={(e) => setDownloadDir(e.target.value)}
                placeholder="/workspace"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">host</Label>
                  <Tooltip content="Network interface vLLM binds to. 0.0.0.0 listens on all interfaces (required for external access on Vast).">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Input
                  className="mt-1"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                  placeholder="0.0.0.0"
                />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">port</Label>
                  <Tooltip content="Port vLLM serves on. Must match the Vast template's exposed port. Default is 8000.">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Input
                  className="mt-1"
                  value={port}
                  onChange={(e) => setPort(e.target.value)}
                  placeholder="8000"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs">Additional vLLM flags</Label>
                  <Tooltip content="Extra --flag value pairs passed to vLLM serve. Use for any argument not listed above (e.g. --max-logprobs, --enable-chunked-prefill).">
                    <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                  </Tooltip>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setExtraArgs((prev) => [...prev, { key: "", value: "" }])
                  }
                >
                  Add
                </Button>
              </div>
              <div className="space-y-2 mt-2">
                {extraArgs.map((row, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      placeholder="--flag"
                      value={row.key}
                      onChange={(e) => {
                        const next = [...extraArgs];
                        next[idx] = { ...next[idx], key: e.target.value };
                        setExtraArgs(next);
                      }}
                    />
                    <Input
                      placeholder="value"
                      value={row.value}
                      onChange={(e) => {
                        const next = [...extraArgs];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setExtraArgs(next);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Speculative Decoding */}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex-1 border-t border-border" />
              <span className="text-xs text-muted-foreground font-gc-med">Speculative Decoding</span>
              <Tooltip content="Reduces inter-token latency (TPS) by speculatively generating multiple tokens per step. Does not improve TTFT. Requires model or method support.">
                <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </Tooltip>
              <div className="flex-1 border-t border-border" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Label className="text-xs">Method</Label>
                <Tooltip content="'qwen3_next_mtp' / 'mtp' — uses model's built-in MTP heads (Qwen3.5, DeepSeek-V3). 'draft_model' — runs a smaller model alongside the target. 'eagle' / 'eagle3' — uses an EAGLE speculator model. 'ngram' — matches n-grams from the prompt (no extra model).">
                  <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                </Tooltip>
              </div>
              <Select value={specMethod} onValueChange={(v) => { setSpecMethod(v); if (v === "none") { setSpecDraftModel(""); setSpecDraftTp(""); } }}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (disabled)</SelectItem>
                  <SelectItem value="qwen3_next_mtp">MTP — Qwen3.5</SelectItem>
                  <SelectItem value="mtp">MTP — generic (DeepSeek-V3)</SelectItem>
                  <SelectItem value="draft_model">Draft model</SelectItem>
                  <SelectItem value="eagle">EAGLE</SelectItem>
                  <SelectItem value="eagle3">EAGLE3</SelectItem>
                  <SelectItem value="ngram">N-gram (no extra model)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {specMethod !== "none" && (
              <>
                <div>
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs">num_speculative_tokens</Label>
                    <Tooltip content="Tokens to speculate per step. For MTP start with 1-2. For draft models 3-5 is common. Higher values increase speculation depth but lower acceptance rate.">
                      <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                    </Tooltip>
                  </div>
                  <Input
                    className="mt-1 w-24"
                    type="number"
                    min={1}
                    max={10}
                    value={specNumTokens}
                    onChange={(e) => setSpecNumTokens(e.target.value)}
                    placeholder="2"
                  />
                </div>
                {["draft_model", "eagle", "eagle3", "mlp"].includes(specMethod) && (
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Label className="text-xs">Draft model</Label>
                      <Tooltip content="HF model ID for the draft/speculator model (e.g. Qwen/Qwen3-0.6B for draft, yuhuili/EAGLE-LLaMA3-Instruct-8B for EAGLE).">
                        <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                      </Tooltip>
                    </div>
                    <Input
                      className="mt-1"
                      value={specDraftModel}
                      onChange={(e) => setSpecDraftModel(e.target.value)}
                      placeholder="org/draft-model-name"
                    />
                  </div>
                )}
                {["eagle", "eagle3"].includes(specMethod) && (
                  <div>
                    <div className="flex items-center gap-1.5">
                      <Label className="text-xs">draft_tensor_parallel_size</Label>
                      <Tooltip content="Tensor parallel size for the draft model. Defaults to 1. Set higher if the draft model needs multiple GPUs.">
                        <InfoIcon className="w-3 h-3 text-muted-foreground cursor-help" />
                      </Tooltip>
                    </div>
                    <Input
                      className="mt-1 w-24"
                      type="number"
                      min={1}
                      value={specDraftTp}
                      onChange={(e) => setSpecDraftTp(e.target.value)}
                      placeholder="1"
                    />
                  </div>
                )}
              </>
            )}
            </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2 py-2 text-sm">
            <p className="text-muted-foreground">Step 4 — Review</p>
            <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(
                {
                  provider,
                  gpu_profile: gpuProfile,
                  gpu_count: gpuCount,
                  hf_model_id: hfModelId,
                  display_name: displayName || baseModel || hfModelId.split("/").pop() || "(derived from HF id)",
                  base_model: baseModel || hfModelId.split("/").pop() || "(derived from HF id)",
                  template: templateHashId
                    ? `${templates.find((t) => t.hash_id === templateHashId)?.name || ""} (${templateHashId})`
                    : "(none selected)",
                  vllm_config: vllmConfig,
                },
                null,
                2
              )}
            </pre>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep((s) => s - 1)}
              disabled={submitting}
            >
              Back
            </Button>
          )}
          {step < 3 && (
            <Button
              type="button"
              onClick={() => {
                if (step === 2 && !hfModelId.trim()) {
                  toast.error("HF model id is required");
                  return;
                }
                setStep((s) => s + 1);
              }}
              disabled={submitting}
            >
              Next
            </Button>
          )}
          {step === 3 && (
            <Button type="button" onClick={submit} disabled={submitting}>
              {submitting ? "Creating…" : "Create instance"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateInstanceWizard;
